import { Link, useLocation } from "react-router-dom";
import { FileWarning, MapPin } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FaqList } from "@/components/FaqList";
import { CtaBand } from "@/components/CtaBand";
import { CategoryTiles, LocationChips } from "@/components/Tiles";
import { PAGES } from "@/content/pages";
import { useSite } from "@/context/Providers";
import { breadcrumbLd, businessLd, faqLd } from "@/lib/seo";
import NotFound from "@/pages/NotFound";

export const PageHeader = ({ crumbs, eyebrow, h1, intro, testid = "page-title" }) => (
  <div className="border-b border-line bg-panel/40">
    <div className="container-x py-12 sm:py-16">
      <Breadcrumbs items={crumbs} />
      {eyebrow && <p className="eyebrow mt-6">{eyebrow}</p>}
      <h1 className="mt-3 max-w-4xl text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl" data-testid={testid}>{h1}</h1>
      {intro && <p className="mt-5 max-w-3xl text-base leading-relaxed text-gray-300 sm:text-lg">{intro}</p>}
    </div>
  </div>
);

export default function ContentPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/|\/$/g, "");
  const page = PAGES[slug];
  const site = useSite();
  if (!page) return <NotFound />;
  const path = `/${slug}/`;
  const crumbs = [{ name: "Home", path: "/" }, { name: page.h1, path }];

  return (
    <>
      <Seo title={page.title} description={page.description} path={path} noindex={page.legal}
        jsonLd={[breadcrumbLd(crumbs), faqLd(page.faqs), page.showMap ? businessLd(site) : null]} />
      <PageHeader crumbs={crumbs} eyebrow={page.eyebrow} h1={page.h1} intro={page.intro} />
      <div className="container-x grid gap-12 py-14 lg:grid-cols-[1.6fr_1fr]">
        <article className="max-w-3xl">
          {page.legal && <p className="mb-8 flex gap-3 rounded-2xl border border-gold/40 bg-gold/5 p-4 text-sm text-gold" role="note" data-testid="legal-placeholder-note"><FileWarning className="h-5 w-5 shrink-0" aria-hidden />Placeholder — final legal copy pending SHADC approval.</p>}
          <div className="rich">
            {page.sections.map((s) => (
              <section key={s.h2}>
                <h2>{s.h2}</h2>
                {s.p.map((t) => <p key={t.slice(0, 30)}>{t}</p>)}
                {s.links && <ul className="!list-none !pl-0 flex flex-wrap gap-2">{s.links.map((l) => <li key={l.to}><Link to={l.to} className="chip !no-underline">{l.label}</Link></li>)}</ul>}
              </section>
            ))}
          </div>
          {page.faqs && <section className="mt-12"><h2 className="mb-5 text-2xl font-semibold text-white">Frequently asked questions</h2><FaqList faqs={page.faqs} id={`${slug}-faq`} /></section>}
        </article>
        <aside className="space-y-6">
          {page.showMap && site && (
            <div className="card-surface p-6">
              <h2 className="font-sans text-xs font-bold uppercase tracking-[0.18em] text-gold">Visit / contact</h2>
              <p className="mt-4 flex gap-3 text-gray-200"><MapPin className="h-5 w-5 text-gold" aria-hidden />{site.address.street}, {site.address.city}, {site.address.region} {site.address.postal}</p>
              <a className="link-gold mt-3 inline-block text-sm" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${site.address.street} ${site.address.city} ${site.address.region} ${site.address.postal}`)}`} target="_blank" rel="noopener noreferrer">Open in Google Maps ↗</a>
            </div>
          )}
          {page.related && (
            <div className="card-surface p-6">
              <h2 className="font-sans text-xs font-bold uppercase tracking-[0.18em] text-gold">Related</h2>
              <ul className="mt-4 space-y-2">{page.related.map((r) => <li key={r.to}><Link to={r.to} className="text-gray-200 hover:text-gold">{r.label} →</Link></li>)}</ul>
            </div>
          )}
          {page.showLocations && <div className="card-surface p-6"><h2 className="mb-4 font-sans text-xs font-bold uppercase tracking-[0.18em] text-gold">Neighborhoods we serve</h2><LocationChips /></div>}
        </aside>
      </div>
      {page.showCategories && <section className="container-x pb-6" aria-labelledby="pc-title"><h2 id="pc-title" className="mb-6 text-3xl font-semibold text-white">Shop by category</h2><CategoryTiles /></section>}
      {!page.legal && <CtaBand />}
    </>
  );
}
